import { isFilled } from "@/ipqc/lib/utils"
import { UseFormReturn, ValidateFn } from "@/lib/form/form"
import { ParamSchema } from "@/lib/form/schema"
import { EnumsOf } from "@/lib/utils"
import { isNaN } from "lodash"
import * as y from "yup"

export const LimitValidationError = {
  OutOfRange: "OutOfRange",
} as const
export type LimitValidationError = EnumsOf<typeof LimitValidationError>

export const limitValiSchema = y
  .object({
    id: y.string().optional(),
    value: y.number().transform((value) => (isNaN(value) ? undefined : value)),
    gradeLabel: y.string().optional(),
    remark: y.string().optional(),
    recordedAt: y.string().optional(),
  })
  .test({
    name: "test-limit-input",
    test: function (value, ctx) {
      const context = ctx.options.context as
        | {
          required: boolean
          schema: ParamSchema
          form: UseFormReturn
          path: string
        }
        | undefined

      if (!context?.schema.limit) {
        return ctx.createError({
          message: `${context?.path} limit not specified in the schema`,
        })
      }

      if (context.required && !isFilled(value.value)) {
        return ctx.createError({ message: `${context.path} value is required` })
      }

      if (typeof value.value !== "number" || isNaN(value.value)) {
        return true
      }

      const minLimit = Math.min(
        context.schema.limit.min,
        context.schema.limit.max
      )
      const maxLimit = Math.max(
        context.schema.limit.min,
        context.schema.limit.max
      )

      if ((value.value < minLimit || value.value > maxLimit) && !value.remark) {
        return ctx.createError({ message: LimitValidationError.OutOfRange })
      }

      return true
    },
  })

export const createLimitValidator = ({
  schema,
  path,
  form,
  isHandover,
  required,
}: {
  schema: ParamSchema
  path: string
  form: UseFormReturn
  isHandover?: boolean
  required?: boolean
}): ValidateFn => {
  return (value: any) => {
    try {
      const finalSchema =
        isHandover || required ? limitValiSchema.required() : limitValiSchema

      if (!schema.limit && schema.subtext) {
        const subtext = schema.subtext as string;

        const [min, max] = subtext.split("-").map(s => Number(s.trim()));

        schema.limit = {
          min: min,
          max: max,
          gradeRanges : []
        };
      }

      finalSchema.validateSync(value, {
        context: {
          required: !!(isHandover || required),
          schema: schema,
          form: form,
          path: path,
        },
      })

      return false
    } catch (err) {
      return err as y.ValidationError
    }
  }
}

export function createValidator(valiSchema: y.Schema): ValidateFn {
  return (value: any, formValues) => {
    try {
      valiSchema.validateSync(value, {
        context: {
          form: formValues,
        },
      })

      return false
    } catch (err) {
      return err as y.ValidationError
    }
  }
}
